import { apiUrl } from "../api/endpoints";
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import SettingsSuggestIcon from '@mui/icons-material/SettingsSuggest';
import { GridActionsCellItem } from '@mui/x-data-grid';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import DataGridTable from '../Components/DataGridTable';
import Spinner from '../Components/Spinner';
import AppDialog from '../Components/AppDialog';

import { useAuth } from '../hooks/AuthProvider';
import { deleteAutomation, deleteMultiAutomation } from '../lib/actions';
import { getErrorMessage } from '../utils/errorUtils';
import { LanguageContext } from '../context/LanguageContext';
import automationsTranslations from '../languages/automation.json';
import { AutomationHeaderIcon } from '../svg/AutomationIcons';
import AutomationBuilderPage from '../Components/automation/AutomationBuilderPage';
import './automations.css';
import './edit.css';
import './details.css';
import { DeleteIcon } from '../svg/UtilsIcons';

const createDialogState = () => ({
  open: false,
  type: 'alert',
  variant: 'info',
  icon: 'alert',
  title: '',
  message: '',
  confirmText: 'Ok',
  cancelText: 'Cancel',
  showCancel: false,
});

export const Automations = () => {
  const navigate = useNavigate();
  const { org, permissions } = useAuth();
  const { lang } = useContext(LanguageContext);

  const t = automationsTranslations[lang];
  const isRtl = ['ar', 'ur'].includes(lang);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refresh, setRefresh] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [dialog, setDialog] = useState(createDialogState());
  const resolverRef = useRef(null);

  const closeDialog = useCallback((result = false) => {
    setDialog((current) => ({ ...current, open: false }));
    if (resolverRef.current) {
      resolverRef.current(result);
      resolverRef.current = null;
    }
  }, []);

  const alert = useCallback(
    (options) =>
      new Promise((resolve) => {
        resolverRef.current = resolve;

        setDialog({
          open: true,
          type: options?.type || 'alert',
          variant: options?.variant || 'info',
          icon: options?.icon || 'alert',
          title: options?.title || '',
          message: options?.message || '',
          confirmText: options?.confirmText || 'Ok',
          cancelText: options?.cancelText || 'Cancel',
          showCancel: Boolean(options?.showCancel),
        });
      }),
    []
  );

  const showAlert = useCallback(async (type, message) => {
    const variant = type === 'error' ? 'error' : type === 'warning' ? 'info' : 'info';

    await alert({
      type: 'alert',
      variant,
      icon: 'alert',
      title:
        type === 'error'
          ? t?.alert?.errorTitle || 'Error'
          : type === 'warning'
            ? t?.alert?.warningTitle || 'Warning'
            : t?.alert?.infoTitle || 'Info',
      message: message || '',
      confirmText: 'Ok',
    });
  }, [alert, t?.alert?.errorTitle, t?.alert?.infoTitle, t?.alert?.warningTitle]);

  const fetchAutomations = useCallback(async (organizationId) => {
    setLoading(true);

    try {
      if (!organizationId) {
        setRows([]);
        return;
      }

      const response = await axios.get(apiUrl(`automation/filtered/${organizationId}`));
      const nextRows = Array.isArray(response.data?.data) ? response.data.data : [];
      setRows(nextRows);

      if (!Array.isArray(response.data?.data)) {
        await showAlert('error', 'Received an invalid format from the server.');
      }
    } catch (error) {
      setRows([]);
      await showAlert('error', getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  }, [showAlert]);

  useEffect(() => {
    setRefresh(false);

    if (org?.id) {
      fetchAutomations(org.id);
      return;
    }

    setLoading(false);
  }, [fetchAutomations, org?.id, refresh]);

  const canDeleteAutomations = Boolean(permissions?.delete_automations);
  const canCreateAutomations = Boolean(permissions?.create_automations);
  const canViewAutomations = Boolean(permissions?.view_automations);

  const columns = useMemo(
    () => [
      ...(canDeleteAutomations
        ? [
            {
              field: 'actions',
              headerName: t.column.actions,
              type: 'actions',
              width: 100,
              getActions: (params) => [
                <GridActionsCellItem
                  key='delete'
                  icon={<DeleteIcon className='auto-icon-danger' />}
                  label={t.delete}
                  onClick={() => deleteAutomation(params.row.id, () => setRefresh(true), alert)}
                  showInMenu
                />,
              ],
            },
          ]
        : []),
      { field: 'id', headerName: t.column.id, align: 'center', headerAlign: 'center', width: 100 },
      { field: 'organization_name', headerName: t.column.organization, align: 'left', headerAlign: 'left', width: 200 },
      { field: 'trigger', headerName: t.column.trigger, align: 'left', headerAlign: 'left', width: 120 },
      { field: 'device_name', headerName: t.column.device, align: 'left', headerAlign: 'left', width: 220 },
      { field: 'description', headerName: t.column.description, align: 'left', headerAlign: 'center', width: 650 },
    ],
    [alert, canDeleteAutomations, t.column.actions, t.column.description, t.column.device, t.column.id, t.column.organization, t.column.trigger, t.delete]
  );

  const selectActions = useMemo(
    () =>
      canDeleteAutomations
        ? [
            {
              title: t.delete,
              icon: <DeleteIcon className='auto-icon-danger auto-icon-danger--spaced' />,
              action: async (ids) => {
                await deleteMultiAutomation(ids, () => setRefresh(true), alert);
                setSelectedRows([]);
              },
            },
          ]
        : [],
    [alert, canDeleteAutomations, t.delete]
  );

  if (!canViewAutomations) {
    return <div>{t.noPermission}</div>;
  }

  return (
    <div className='configuration devices-page auto-page' dir={isRtl ? 'rtl' : 'ltr'}>
      <AppDialog
        open={dialog.open}
        type={dialog.type}
        variant={dialog.variant}
        icon={dialog.icon}
        title={dialog.title}
        message={dialog.message}
        confirmText={dialog.confirmText}
        cancelText={dialog.cancelText}
        showCancel={dialog.showCancel}
        onConfirm={() => closeDialog(true)}
        onCancel={() => closeDialog(false)}
        onClose={() => closeDialog(false)}
      />

      <div className='device-main'>
        <div className='devices-header'>
          <div className='devices-header-left'>
            <span className='auto-header-icon'>
              <AutomationHeaderIcon />
            </span>
            <h2 className='devices-title'>{t.automationsPageTitle}</h2>
          </div>

          {canCreateAutomations ? (
            <button className='devices-primary-btn' onClick={() => navigate('/automations/new')}>
              <SettingsSuggestIcon />
              <span>{t.buttonNew}</span>
            </button>
          ) : null}
        </div>

        {loading ? (
          <Spinner />
        ) : rows.length > 0 ? (
          <div className='devices-grid-wrapper auto-grid-wrapper'>
            <DataGridTable
              columns={columns}
              rows={rows}
              idcolumn='id'
              pagingoptions={[10, 20, 50, 100]}
              handleRowClick={(params) => navigate(`/automations/details/${params.row.id}`)}
              checkboxSelection={canDeleteAutomations}
              rowSelectionModel={selectedRows}
              setRowSelectionModel={setSelectedRows}
              selectActions={selectActions}
              showToolbar={true}
              tableKey='automations'
            />
          </div>
        ) : (
          <div className='auto-no-data'>{t.noData}</div>
        )}
      </div>
    </div>
  );
};

export const NewAutomation = () => <AutomationBuilderPage mode='create' />;

export const AutomationDetails = () => {
  const { id } = useParams();
  return <AutomationBuilderPage mode='edit' automationId={id} />;
};
